import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemText,
  debounce,
} from '@mui/material';
import { uniqueBy } from '@realadvisor/array';

import { RaAutoComplete } from '@modules/_shared/components/data-grid/RaAutoComplete';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { useLocale } from '@shared/hooks/locale';

import { GET_LISTING_BUYERS } from './brochureRecipientsQueries';

type BuyerOption = { userId: string; name: string };

export const ListingBuyerSelect = ({
  lotId,
  userId,
  onChange,
  onBuyersCountChange,
  placeholder,
}: {
  lotId: string;
  userId: string | null;
  onChange: (userId: string | null) => void;
  onBuyersCountChange?: (count: number) => void;
  placeholder?: string;
}) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<BuyerOption[]>([]);
  const searchRef = useRef('');

  const { data, loading, error, refetch } = useQuery(GET_LISTING_BUYERS, {
    variables: { lotId },
    skip: lotId === '',
  });

  const allBuyers = useMemo<BuyerOption[]>(
    () => [
      ...uniqueBy(
        (data?.buyer_leads ?? []).flatMap(({ user }) => {
          if (user == null) {
            return [];
          }

          return [
            {
              userId: user.id,
              name: getUserDisplayName(user, true) || t('Unnamed buyer'),
            },
          ];
        }),
        option => option.userId,
      ),
    ],
    [data, t],
  );

  useEffect(() => {
    if (!loading && error == null && data != null) {
      onBuyersCountChange?.(allBuyers.length);
    }
  }, [loading, error, data, allBuyers, onBuyersCountChange]);

  const value = allBuyers.find(option => option.userId === userId) ?? null;

  const handleSearch = useCallback(
    (search?: string) => {
      searchRef.current = search ?? '';
      const query = (search ?? '').trim().toLowerCase();
      setOptions(
        query === ''
          ? allBuyers
          : allBuyers.filter(option =>
              option.name.toLowerCase().includes(query),
            ),
      );
      setOpen(true);
    },
    [allBuyers],
  );

  const debouncedSearch = useMemo(
    () => debounce(handleSearch, 150),
    [handleSearch],
  );

  useEffect(() => () => debouncedSearch.clear(), [debouncedSearch]);

  useEffect(() => {
    if (open) {
      handleSearch(searchRef.current);
    }
  }, [handleSearch, open]);

  return (
    <>
      {error != null && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void refetch().catch(() => {})}
            >
              {t('Retry')}
            </Button>
          }
        >
          {t('somethingWentWrong')}
        </Alert>
      )}
      <RaAutoComplete<BuyerOption>
        InputProps={{ placeholder: placeholder ?? t('Search buyers') }}
        open={open}
        setOpen={setOpen}
        options={options}
        setOptions={setOptions}
        loading={loading}
        value={value}
        onOpen={() => handleSearch('')}
        onInputChange={(_event, inputValue, reason) => {
          if (reason === 'input') {
            searchRef.current = inputValue;
          } else if (reason === 'clear') {
            handleSearch('');
          }
        }}
        allowEmptySearch
        debouncedSearch={debouncedSearch}
        onChange={(_event, option) => onChange(option?.userId ?? null)}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, selected) =>
          option.userId === selected.userId
        }
        noOptionsText={t('No buyers for this listing')}
        renderOption={(props, option) => (
          <ListItem {...props} key={option.userId} sx={{ padding: '1px 10px' }}>
            <ListItemAvatar>
              <UserAvatar
                nameFallback={option.name}
                size={32}
                sx={{ fontSize: 13 }}
              />
            </ListItemAvatar>
            <ListItemText
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              primary={option.name}
            />
          </ListItem>
        )}
      />
    </>
  );
};
